import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
// @mui
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Unstable_Grid2";
import {alpha, useTheme} from '@mui/material/styles';
import { Box } from '@mui/system';
import { Card, CardContent, CardHeader, List, ListItem, ListItemText, Skeleton, Typography } from '@mui/material';
// components
import { useSettingsContext } from '../../components/settings';

import { getFinancialData } from '../../components/upload';
import CreditScore from './charts/chart-credit-score';
import Iconify from '../../components/iconify';

export default function FinancialCreditProfile() {
    const navigate = useNavigate();
    const settings = useSettingsContext()
    const theme = useTheme();
    
    const [resumen, setResumen] = useState(null);

    const [isGenerating, setIsGenerating] = useState(true);

    const aiValid = JSON.parse(localStorage.getItem("ai_score_valid"));

    useEffect(() => {
        let checkInterval; 

        try {
            const data = getFinancialData();
            if (!data) throw new Error('No data');
            
            setResumen(data);
            settings.onFiles();
            settings.onValid();

            if (localStorage.getItem("ai_score_valid") !== null) {
                setIsGenerating(false);
            } else {
                checkInterval = setInterval(() => {
                    if (localStorage.getItem("ai_score_valid") !== null) {
                        setIsGenerating(false);
                        clearInterval(checkInterval);
                    }
                }, 1000);
            }
        } catch (error) {
            console.error(error);
            navigate('/financial');
        }

        return () => {
            if (checkInterval) clearInterval(checkInterval);
        };
    }, [navigate, settings]);

    if (!resumen) return null;

    let businessRulesPassed = resumen.credito.allPassed;
    if (!isGenerating) {
        businessRulesPassed = resumen.credito.allPassed && aiValid !== false;
    }
    const recomendacionFinal = !isGenerating && aiValid === false ? "RECHAZAR" : resumen.credito.recomendacion;

    const rulesLeft = [
        { text: "Desempleado", passed: resumen.credito.reglas.regla_desempleo },
        { text: "Ingresos limitados", passed: resumen.credito.reglas.regla_ingresos },
        { text: "Endeudamiento elevado", passed: resumen.credito.reglas.regla_endeudamiento },
    ];

    const rulesRight = [
        { text: "Saldo reducido", passed: resumen.credito.reglas.regla_saldo_reducido },
        { text: "Con impagos en sistema", passed: resumen.credito.reglas.regla_impagos },
        { text: "Hábitos inadecuados", passed: resumen.credito.reglas.regla_gambling },
    ];

    const recomendacionColor = {
        "RECHAZAR": theme.palette.red.main,
        "REVISAR": theme.palette.warning.light,
        "APROBAR": theme.palette.tertiary.main,
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardHeader title="Modelo proforma" sx={{ textAlign: "center", flex: "0 0 auto", pb: 0 }} />
                    <CardContent sx={{ textAlign: "center", pt: 5 }}>
                        <CreditScore score={resumen.credito.score} thresholds={5} />
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12} md={4}>
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardHeader title="Reglas de negocio" sx={{ textAlign: "center", flex: "0 0 auto", pb: 0 }} />
                    <CardContent
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                        >
                        {businessRulesPassed === true ? 
                            <Typography
                                variant="h2"
                                align="center"
                                color={theme.palette.tertiary.main}
                            >
                                OK
                            </Typography>
                            :
                            <Typography
                                variant="h2"
                                align="center"
                                color={theme.palette.red.main}
                            >
                                KO
                            </Typography>
                        }
                    </CardContent>
                </Card>
            </Grid>
            <Grid item xs={12} md={4} >
                <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <CardHeader title="Recomendación" sx={{ textAlign: "center", flex: "0 0 auto", pb: 0 }} />
                    <CardContent
                        sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}
                        >
                        <Typography
                            variant="h2"
                            align="center"
                            color={recomendacionColor[recomendacionFinal]}
                        >
                            {recomendacionFinal}
                        </Typography>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    transition: 'all 0.3s ease-in-out',
                    border: (t) => {
                        if (isGenerating) return `1px dashed ${t.palette.divider}`;
                        return `1px solid ${aiValid === false ? t.palette.red.light : t.palette.divider}`;
                    },
                    bgcolor: (t) => {
                        if (isGenerating) return alpha(t.palette.background.neutral, 0.5);
                        return aiValid === false ? alpha(t.palette.red.main, 0.04) : alpha(t.palette.tertiary.main, 0.04);
                    },
                }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: (t) => isGenerating ? alpha(t.palette.grey[500], 0.1) : alpha(aiValid === false ? t.palette.red.main : t.palette.tertiary.main, 0.1) 
                        }}>
                            {isGenerating ? (
                                <Iconify icon="eos-icons:loading" width={32} color="text.disabled" />
                            ) : (
                                <Iconify 
                                    icon={aiValid === false ? "solar:shield-warning-bold-duotone" : "solar:shield-check-bold-duotone"} 
                                    width={32} 
                                    color={aiValid === false ? 'red.main' : 'tertiary.main'} 
                                />
                            )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="h6">Análisis del agente</Typography>
                            {isGenerating ? (
                                <Skeleton variant="text" width="60%" height={20} />
                            ) : (
                                <Typography variant="body2" sx={{ color: aiValid === false ? 'red.main' : 'text.secondary' }}>
                                    {aiValid === false 
                                        ? "Se han detectado patrones inconsistentes que sugieren el ocultamiento de información financiera." 
                                        : "La cuenta analizada presenta un comportamiento coherente con una cuenta corriente principal."}
                                </Typography>
                            )}
                        </Box>
                        {!isGenerating && (
                            <Typography variant="h5" sx={{ 
                                color: aiValid === false ? 'red.main' : 'tertiary.main',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                ml: 2
                            }}>
                                {aiValid === false ? 'Fraude Potencial' : 'Verificado'}
                            </Typography>
                        )}
                        {isGenerating && <Skeleton variant="rectangular" width={100} height={30} sx={{ borderRadius: 1 }} />}
                    </Stack>
                </Card>
            </Grid>

            <Grid item xs={12}>
                <Card>
                    <CardHeader title="Reglas de la agregación" sx={{ pb: 3 }} />
                    <CardContent>
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={6}>
                                {rulesLeft.map((rule, idx) => (
                                    <RuleRow key={idx} text={rule.text} passed={!rule.passed} />
                                ))}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                {rulesRight.map((rule, idx) => (
                                    <RuleRow key={idx} text={rule.text} passed={!rule.passed} />
                                ))}
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
}

// Componente pequeño para las filas de reglas para limpiar el código
function RuleRow( { text, passed } ) {
    const theme = useTheme();
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'background.neutral' }}>
            <Typography variant="subtitle1">{text}</Typography>
            <Iconify 
                icon={passed ? "mdi:check-circle" : "material-symbols:cancel"} 
                color={passed ? theme.palette.tertiary.main : theme.palette.red.main}
                width={24}
            />
        </Stack>
    );
}

RuleRow.propTypes = {
    text: PropTypes.string.isRequired,
    passed: PropTypes.bool.isRequired,
};

